import { createSlice } from '@reduxjs/toolkit';
import { safeJsonParse } from '../../utils/safeJsonParse';

const initialState = {
  others: [],
  imgs_for_filters: [],
  running_line_content: [],
  author_cards_meta: [],
  loading: false,
  error: null,
};

const othersDataSlice = createSlice({
  name: 'others_data',
  initialState,
  reducers: {
    setOthers: (state, action) => {
      // console.log('setOthers action called with payload:', action.payload);

      // Сначала устанавливаем others, чтобы не потерять данные
      state.others = action.payload;
      state.running_line_content = [];

      // Проверяем, есть ли данные для обработки
      if (!action.payload || !Array.isArray(action.payload)) {
        // console.warn('setOthers: action.payload is not an array or is empty');
        state.imgs_for_filters = [];
        return;
      }

      // Ищем элементы с tab_tag === 'running_line_content' и сохраняем в Redux
      // только список публикаций (без родительской обёртки other)
      if (Array.isArray(action.payload)) {
        const runningLineParents = action.payload.filter(
          (el) => el && el.tab_tag === 'running_line_content',
        );

        const runningLinePublications = [];

        runningLineParents.forEach((parent) => {
          const dataSource =
            Array.isArray(parent?.data) ? parent.data : safeJsonParse(parent?.data, null);
          const contentSource = Array.isArray(parent?.content)
            ? parent.content
            : safeJsonParse(parent?.content, null);

          const extracted = [];

          if (Array.isArray(dataSource)) {
            extracted.push(...dataSource.filter((item) => item && typeof item === 'object'));
          }
          if (Array.isArray(contentSource)) {
            extracted.push(...contentSource.filter((item) => item && typeof item === 'object'));
          }

          const blockContentGroups = extracted
            .map((publication) => {
              const rawContent = publication?.content;
              if (Array.isArray(rawContent)) return rawContent;
              const parsedContent = safeJsonParse(rawContent, null);
              return Array.isArray(parsedContent) ? parsedContent : null;
            })
            .map((contentArr) => {
              if (!Array.isArray(contentArr) || !contentArr[0]) return [];
              const firstContent = contentArr[0];
              if (!Array.isArray(firstContent.block_content)) return [];
              const blocks = firstContent.block_content;

              const normalizeTextValue = (value) => {
                if (typeof value !== 'string') return '';
                const normalized = value.trim();
                if (!normalized || normalized.toLowerCase() === 'none') return '';
                return normalized;
              };

              const findBlockByTagOrName = (matcher) =>
                blocks.find((block) => matcher((block?.content_tag || '').toLowerCase(), (block?.content_name || '').toLowerCase()));

              const textBlock =
                findBlockByTagOrName((tag, name) => tag === 'text' || name.includes('текст')) ||
                blocks.find((block) => normalizeTextValue(block?.content_text));

              const text = normalizeTextValue(textBlock?.content_text);
              if (!text) return [];

              const linkBlock = findBlockByTagOrName((tag, name) => tag === 'link' || name.includes('ссылка'));

              const linkFromLinkBlockText = normalizeTextValue(linkBlock?.content_text);
              const linkFromLinkBlockLinks = normalizeTextValue(linkBlock?.links_names);
              const linkFromAnyLinks = blocks
                .map((block) => normalizeTextValue(block?.links_names))
                .find(Boolean);

              const resolvedLink =
                linkFromLinkBlockText || linkFromLinkBlockLinks || linkFromAnyLinks || '';

              // Добавляем остальные значения контент-блока после text/link
              // (например place, author и т.д.), сохраняя порядок.
              const extraContentValues = blocks
                .filter((block) => block !== textBlock && block !== linkBlock)
                .map((block) => normalizeTextValue(block?.content_text))
                .filter(Boolean);

              return [[text, resolvedLink, ...extraContentValues]];
            });

          if (blockContentGroups.length > 0) {
            runningLinePublications.push(...blockContentGroups);
          }
        });

        state.running_line_content = runningLinePublications;
        // console.log('[redux running_line_content]', state.running_line_content);
      }

      // Ищем элементы с eng_name === 'img_for_filter'
      const imgsForFilter = action.payload.filter((rl) => rl.eng_name === 'img_for_filter');

      if (imgsForFilter.length === 0) {
        // console.warn('setOthers: no elements with eng_name === "img_for_filter" found');
        state.imgs_for_filters = [];
        return;
      }

      // Проверяем, что у первого элемента есть content
      if (!imgsForFilter[0] || !imgsForFilter[0].content) {
        // console.warn('setOthers: first img_for_filter element has no content');
        state.imgs_for_filters = [];
        return;
      }

      try {
        const filterContent = JSON.parse(imgsForFilter[0].content);

        if (!filterContent || !Array.isArray(filterContent) || filterContent.length === 0) {
          // console.warn('setOthers: filterContent is not a valid array');
          state.imgs_for_filters = [];
          return;
        }

        if (
          !filterContent[0] ||
          !filterContent[0].block_content ||
          !Array.isArray(filterContent[0].block_content)
        ) {
          // console.warn('setOthers: filterContent[0].block_content is not a valid array');
          state.imgs_for_filters = [];
          return;
        }

        if (!filterContent[0].block_content[0] || !filterContent[0].block_content[0].imgs_names) {
          // console.warn('setOthers: filterContent[0].block_content[0].imgs_names is missing');
          state.imgs_for_filters = [];
          return;
        }

        // Проверяем на "none" перед парсингом
        const imgsNamesValue = filterContent[0].block_content[0].imgs_names;
        if (!imgsNamesValue || imgsNamesValue.trim() === 'none') {
          state.imgs_for_filters = [];
          return;
        }

        const f_imgs = safeJsonParse(imgsNamesValue, []);
        const path = imgsForFilter[0].path;
        const blank_name = imgsForFilter[0].blank_name;

        if (!Array.isArray(f_imgs)) {
          // console.warn('setOthers: f_imgs is not an array');
          state.imgs_for_filters = [];
          return;
        }

        const processedImgs = f_imgs.map((imgPath) => {
          const parts = imgPath.split(/[/\\]/);
          const fileName = parts[parts.length - 1];
          return 'https://' + path + blank_name + '/' + fileName;
        });

        // Сигнатуры по индексу (как ссылки в главном слайдере) — в word
        let signaturesArr = [];
        const imgsBlock = filterContent[0].block_content[0];
        if (imgsBlock?.imgs_signatures && imgsBlock.imgs_signatures !== 'none' && imgsBlock.imgs_signatures !== '[]') {
          try {
            const parsed = typeof imgsBlock.imgs_signatures === 'string'
              ? JSON.parse(imgsBlock.imgs_signatures)
              : imgsBlock.imgs_signatures;
            signaturesArr = Array.isArray(parsed) ? parsed : [];
          } catch {
            signaturesArr = [];
          }
        }

        const imgsForFiltersArray = processedImgs.map((el, i) => {
          const raw = signaturesArr[i];
          const word = raw != null && String(raw).trim() !== '' && String(raw).trim().toLowerCase() !== 'none'
            ? String(raw).trim()
            : (filterContent[0].block_content[i + 1]?.content_text || '').trim();
          return { img: el, word: word || '' };
        });

        state.imgs_for_filters = imgsForFiltersArray;
        // console.log('state.others', state.others);
        // console.log('imgs_for_filters processed successfully:', state.imgs_for_filters);
      } catch (error) {
        // console.error('setOthers: Error processing filter content:', error);
        state.imgs_for_filters = [];
      }
    },
    addOther: (state, action) => {
      state.others.push(action.payload);
    },
    removeOther: (state, action) => {
      state.others = state.others.filter((other) => other.id !== action.payload);
    },
    updateOther: (state, action) => {
      const { id, updates } = action.payload;
      const otherIndex = state.others.findIndex((other) => other.id === id);
      if (otherIndex !== -1) {
        state.others[otherIndex] = { ...state.others[otherIndex], ...updates };
      }
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearOthers: (state) => {
      state.others = [];
      state.error = null;
    },
    setImgsForFilters: (state, action) => {
      state.imgs_for_filters = action.payload;
    },
    setRunningLineContent: (state, action) => {
      state.running_line_content = Array.isArray(action.payload) ? action.payload : [];
    },
    setAuthorCardsMeta: (state, action) => {
      state.author_cards_meta = Array.isArray(action.payload) ? action.payload : [];
      console.log('[redux author_cards_meta][setAuthorCardsMeta]', state.author_cards_meta);
    },
    addImgForFilter: (state, action) => {
      state.imgs_for_filters.push(action.payload);
    },
    removeImgForFilter: (state, action) => {
      state.imgs_for_filters = state.imgs_for_filters.filter((img) => img.id !== action.payload);
    },
    updateImgForFilter: (state, action) => {
      const { id, updates } = action.payload;
      const imgIndex = state.imgs_for_filters.findIndex((img) => img.id === id);
      if (imgIndex !== -1) {
        state.imgs_for_filters[imgIndex] = { ...state.imgs_for_filters[imgIndex], ...updates };
      }
    },
    clearImgsForFilters: (state) => {
      state.imgs_for_filters = [];
    },
    clearRunningLineContent: (state) => {
      state.running_line_content = [];
    },
    clearAuthorCardsMeta: (state) => {
      state.author_cards_meta = [];
    },
  },
});

export const {
  setOthers,
  addOther,
  removeOther,
  updateOther,
  setLoading,
  setError,
  clearOthers,
  setImgsForFilters,
  setRunningLineContent,
  setAuthorCardsMeta,
  addImgForFilter,
  removeImgForFilter,
  updateImgForFilter,
  clearImgsForFilters,
  clearRunningLineContent,
  clearAuthorCardsMeta,
} = othersDataSlice.actions;

export default othersDataSlice.reducer;
